import { type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { joinNonEmptyStringParts } from '@makemydeal/dr-dash-ui-utils';
import { FINANCE, type GenericObject, type IVehicle } from '@makemydeal/dr-platform-types';

import { getDealTypeLabel } from '../../dealTypeDropdown/utils/getDealTypeLabels';
import { MONEY_FACTOR, MONEY_FACTOR_TYPE } from '../../../constants';
import { HeaderContainer } from './HeaderContainer.style';

export type SlideOutHeaderType = {
    title: ReactNode | string;
    /** Set by hosts showing a not-yet-committed vehicle (e.g. First Pencil) instead of the deal's own. */
    vehicleOverride?: Pick<IVehicle, 'condition' | 'make' | 'year' | 'model' | 'trim'>;
};

export const SlideOutHeader = ({ title, vehicleOverride }: SlideOutHeaderType) => {
    const committedVehicle = useSelector(vehicleSelectors.getVehicle);
    const { condition, make, year, model, trim } = vehicleOverride ?? committedVehicle;

    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const totalAmountDue = useSelector(offerReduxSelectors.getCashTotalAmountDueAfterCredits) ?? 0;
    const amountPaidByCustomer = useSelector(offerReduxSelectors.getCashDueFromCustomer);
    const months = useSelector(offerReduxSelectors.getSelectedTermMonths);
    const monthlyPayment = useSelector(offerSelectors.getMonthlyPayment);
    const downPayment = useSelector((state: GenericObject) => offerReduxSelectors.getDownPaymentByOfferType(state, FINANCE)) ?? 0;
    const apr = useSelector(offerReduxSelectors.getSellRate);
    const rateType = useSelector(offerReduxSelectors.getRateType);
    const moneyFactor = useSelector(offerReduxSelectors.getMoneyFactor);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const isFlexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const currentFlexBuyPayments = useSelector(offerReduxSelectors.getCurrentFlexBuyPayments);
    const firstPaymentTerm = useSelector(offerReduxSelectors.getFirstPaymentTerm);
    const firstPaymentAmount = useSelector(offerReduxSelectors.getFirstPaymentAmount);
    const secondPaymentTerm = useSelector(offerReduxSelectors.getSecondPaymentTerm);
    const secondPaymentAmount = useSelector(offerReduxSelectors.getSecondPaymentAmount);
    const cashDown = useSelector(offerReduxSelectors.getDealDeposit);
    const isOnePayLease = useSelector(offerReduxSelectors.isOnePayLease);
    const dueAtSigning = useSelector(offerReduxSelectors.getDueAtSigning) ?? 0;
    const balloonAmount = useSelector(offerReduxSelectors.getBalloonAmount);

    const isMoneyFactor = rateType === MONEY_FACTOR_TYPE;
    const rateTypeLabel = isMoneyFactor ? ` ${MONEY_FACTOR}` : ` APR`;
    const rateTypeVal = isMoneyFactor ? moneyFactor : apr;

    const flexBuyDetails = useMemo(() => {
        // if payment grid is enabled and current flex buy payments is present:
        if (isFlexBuyPaymentGridEnabled && currentFlexBuyPayments && isFlexBuyPayment) {
            return {
                isFlexBuy: isFlexBuyPayment,
                firstTerm: currentFlexBuyPayments.firstTermPaymentDetail?.term,
                firstPayment: currentFlexBuyPayments.firstTermPaymentDetail?.paymentAmount,
                secondTerm: currentFlexBuyPayments.secondTermPaymentDetail?.term,
                secondPayment: currentFlexBuyPayments.secondTermPaymentDetail?.paymentAmount
            };
        }

        // no payment grid scenario:
        return {
            isFlexBuy: isFlexBuyPayment,
            firstTerm: firstPaymentTerm,
            firstPayment: firstPaymentAmount,
            secondTerm: secondPaymentTerm,
            secondPayment: secondPaymentAmount
        };
    }, [
        isFlexBuyPayment,
        isFlexBuyPaymentGridEnabled,
        currentFlexBuyPayments,
        firstPaymentTerm,
        firstPaymentAmount,
        secondPaymentTerm,
        secondPaymentAmount
    ]);

    const [line1, line2] = getDealTypeLabel({
        currentOfferType,
        totalAmountDue,
        amountPaidByCustomer,
        months,
        monthlyPayment,
        downPayment,
        rateTypeVal,
        rateTypeLabel,
        isFlexBuyPayment,
        flexBuyDetails,
        cashDown: cashDown,
        isOnePayLease,
        dueAtSigning,
        balloonAmount
    });

    const vehicleInfoTitle = joinNonEmptyStringParts(condition, year, make, model, trim);
    const dealTypeTitle = joinNonEmptyStringParts(line1, line2);
    const subtitle = [vehicleInfoTitle, dealTypeTitle].filter(Boolean).join(' - ');

    return (
        <HeaderContainer>
            <Typography variant={'h3'}>{title}</Typography>
            <Typography variant={'body-sm'}>{subtitle}</Typography>
        </HeaderContainer>
    );
};
